import { Injectable } from '@angular/core';
import { HttpClient, } from '@angular/common/http';
import { Observable, BehaviorSubject } from 'rxjs';
import { environment } from '@environments/environment';
import { GlobalFunctions } from '@global/GlobalFunctions';


/**
 * Interface for Database Account Object
 * @help
 * let account = this.accounts.find(({ id }) => id === "<ID String>")
 */
export interface IWebhookIntegration {
	/** Account UID */
	AccountGUID: string
	/** Organisation Webhook ID */
	OrganisationWebhookID: number
	/** Webhook UID */
	WebhookUID: string
	/** Organisation ID */
	OrganisationID: number
	/** Organisation ID */
	OrganisationUID: string
	/** Webhook ID */
	WebhookID: number
	/** Client WWebhook ID */
	ClientWebhookID: string
	/** HTTP Host Name */
	Host: string
	/** Path on Host */
	Path: string
	/** fully Qualified Path Name*/
	FQN: string
	/** Webhook Description */
	Description: string
	/** Auto Build Sensors */
	AutoBuild: boolean
	/** Webhook Enabled */
	Enabled: boolean
	/** Webhook Type ID*/
	WebhookTypeID: number
	/** Webhook Type*/
	Type: string
	/** Webhook Type Path */
	TypePath: string
	/** Webhook Type Description*/
	TypeDescription: string
	/** Type Azure Security Code */
	TypeAzureCode: string
	/** Webhook Deleted */
	Deleted: boolean
	/** Webhook Sensor Count */
	SensorCount: number
	/** Webhook Sensor Count (Offline) */
	SensorOnlineCount: number
	/** Webhook Datapoint Count */
	DatapointCount: number
	/** Webhook Datapoint Count (Offline) */
	DatapointOnlineCount: number
	/** Neuron Sensor API */
	NeuronSensorAPI: string
	/** Neuron Sensor System*/
	NeuronSensorSystem: string
	/** Webhook is a Neuron Sensors Webhook */
	NeuronSensors: boolean
	/** Last Value Received  */
	LastValueReceived: number
	/** Webhook Offline */
	Offline: boolean


	OrganisationName?: string
	AccountName?: string
}

/**
 * IIntegrationType
 */
export interface IIntegrationType {
	/** Webhook Type ID */
	WebhookTypeID: number
	/** Webhook Type*/
	Type: string
	/** Webhook Type Description*/
	Description: string
	/** HTTP Host Name */
	Host: string
	/** Path on Host */
	Path: string
	/** Azure Security Code */
	AzureCode: string
	/** Type is NeuronSensors? */
	NeuronSensors: boolean
	/** Type is configured for use (Creation) */
	IsAllowed: boolean
	/** Type has been used */
	IsUsed: boolean
}

/**
 * IntegrationsService
 */
@Injectable({
	providedIn: 'root'
})
export class IntegrationsService {

	/** Enable Console Messages */
	private Debug: boolean = false

	/** Default Account */
	private defaultAccountSubject: BehaviorSubject<any>;

	/** Default Account for User (JSON FORMAT) */
	public defaultAccount$: Observable<string>;

	/**
	 * Log message to the Console
	 * @param str
	 */
	consoleLog(str: any) { if (this.Debug == true) console.log(str) }

	/** API Route */
	apiRoute: string = "api/{0}/organisation/{1}/integrations"
	apiRouteTypes: string = "api/integrationTypes"

	/**
	 * Default Constructor
	 * @param http Imports the 'HttpClient' Object for internet requests
	 * @param globalFunctions globalFunctions
	 */
	constructor(
		private http: HttpClient,
		private globalFunctions: GlobalFunctions
	) { }


	/**
	 * Service to return a list of Webhook Integration for this Organistion
	 * @param accountGUID Account GUID
	 * @param organisationID Organisadion UID
	 * @param queryParams Optional Query Parameters
	 * @returns IWebhookIntegraton[] Array of Webhooks
	 */
	getWebhookIntegrations$(accountGUID: string, organisationID: string, webhookID: string = "", queryParams: {} = {}): Observable<any> {

		let url = this.apiRoute;
		url = url.replace("{0}", accountGUID);
		url = url.replace("{1}", organisationID);

		if (webhookID != "") { url = `${url}/webhookUID/${webhookID}` }
		url = `${environment.apiUrl}/${url}`;
		return this.globalFunctions.fetchAPI(this.http, url, "GET", queryParams)
	}

	/**
	 * Service to return a list of Webhook Integration Types for this Organistion
	 * @param queryParams Optional Query Parameters
	 * @returns IWebhookIntegraton[] Array of Webhooks
	 */
	getWebhookIntegrationTypes$(queryParams: {} = {}): Observable<any> {

		let url = `${environment.apiUrl}/${this.apiRouteTypes}`;
		return this.globalFunctions.fetchAPI(this.http, url, "GET", queryParams)
	}

	/**
	 * Service to patch a Webhook Integration for this Organistion
	 * @param accountGUID Account GUID
	 * @param organisationID Organisadion UID
	 * @param integrationID Integration ID
	 * @param integration Integration Object
	 * @returns IWebhookIntegraton[] Array of Webhooks
	 */
	patchWebhookIntegration$(accountGUID: string, organisationID: string, integrationID: number, integration: IWebhookIntegration): Observable<any> {

		let data = JSON.stringify(integration);

		let url = this.apiRoute;
		url = url.replace("{0}", accountGUID);
		url = url.replace("{1}", organisationID);

		url = `${environment.apiUrl}/${url}/${integrationID}`;
		return this.globalFunctions.fetchAPI(this.http, url, "PATCH", data)
	}

	/**
	 * Service to post a Webhook Integration for this Organistion
	 * @param accountGUID Account GUID
	 * @param organisationID Organisadion UID
	 * @param integration Integration Object
	 * @returns IWebhookIntegraton[] Array of Webhooks
	 */
	postWebhookIntegration$(accountGUID: string, organisationID: string, integration: IWebhookIntegration): Observable<any> {

		let data = JSON.stringify(integration);

		let url = this.apiRoute;
		url = url.replace("{0}", accountGUID);
		url = url.replace("{1}", organisationID);

		url = `${environment.apiUrl}/${url}`;
		return this.globalFunctions.fetchAPI(this.http, url, "POST", data)
	}

	/**
	 * Service to Delete a Webhook Integration from this Organistion
	 * @param accountGUID Account GUID
	 * @param organisationID Organisadion UID
	 * @param integrationID Integration ID to Delete
	 * @returns IWebhookIntegraton[] Array of Webhooks
	 */
	deleteWebhookIntegration$(accountGUID: string, organisationID: string, integrationID: number,): Observable<any> {

		let url = this.apiRoute;
		url = url.replace("{0}", accountGUID);
		url = url.replace("{1}", organisationID);

		url = `${environment.apiUrl}/${url}/${integrationID}`;
		return this.globalFunctions.fetchAPI(this.http, url, "DELETE", {})
	}

	/**
	 * enableAllSensorsForWebhookIntegration
	 * @param accountGUID accountGUID 
	 * @param organisationID organisationID  
	 * @param webhookUID webhookUID 
	 * @param enable enable 
	 * @returns 
	 */
	enableAllSensorsForWebhookIntegration$(accountGUID: string, organisationID: string, webhookUID: string, enable: boolean, enableDPs: boolean = false, sensorIDList?: string): Observable<any> {
		const data = JSON.stringify(sensorIDList ?? '');

		let url = this.apiRoute;
		url = url.replace("{0}", accountGUID);
		url = url.replace("{1}", organisationID);

		url = `${environment.apiUrl}/${url}/${webhookUID}/allSensors`;

		url += `?enable=${enable}&enableDPs=${enableDPs}`;

		return this.globalFunctions.fetchAPI(this.http, url, "PATCH", data)
	}

}
